import { useState, useRef, useEffect } from "react";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/20/solid";

const StudentFilters = ({ label, options, selected, setSelected }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      setSelected(selected.filter((item) => item !== option));
    } else {
      setSelected([...selected, option]);
    }
  };

  return (
    <div className="relative w-60" ref={dropdownRef}>
      {/* Fixed Select Box */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between border rounded-lg px-3 py-2 bg-white shadow-sm text-sm text-gray-600 hover:border-gray-400 w-full"
      >
        <span className={selected.length > 0 ? "text-gray-600" : "text-gray-400"}>
          {selected.length > 0 ? `${selected.length} selected` : label}
        </span>
        <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-2" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 mt-2 w-full bg-white border rounded-lg shadow-lg z-10 p-2 max-h-56 overflow-y-auto">
          {options.map((option) => (
            <label
              key={option}
              className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => toggleOption(option)}
                className="h-4 w-4"
              />
              {option}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Filters() {
  // Config for filters
  const filterConfigs = [
    { key: "apps", label: "All Applications", options: ["Math App", "Science App", "English App"], color: "blue" },
    { key: "grades", label: "All Grades", options: ["Grade 1", "Grade 2", "Grade 3"], color: "green" },
    { key: "status", label: "All Status", options: ["Active", "Inactive", "Pending"], color: "purple" },
    { key: "adoption", label: "All Adoption Levels", options: ["Low", "Medium", "High"], color: "orange" },
  ];

  // State for all filters
  const [selectedFilters, setSelectedFilters] = useState({
    apps: [],
    grades: [],
    status: [],
    adoption: [],
  });

  const setFilter = (key, value) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    const cleared = {};
    filterConfigs.forEach((f) => (cleared[f.key] = []));
    setSelectedFilters(cleared);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Filter Inputs Row */}
      <div className="flex items-center gap-3 flex-wrap">
        {filterConfigs.map((filter) => (
          <StudentFilters
            key={filter.key}
            label={filter.label}
            options={filter.options}
            selected={selectedFilters[filter.key]}
            setSelected={(value) => setFilter(filter.key, value)}
          />
        ))}

        {/* Clear Filters */}
        <button
          onClick={clearFilters}
          className="flex items-center gap-1 text-primary-600 text-sm hover:underline ml-2"
        >
          <span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M22.122 13.465L23.536 14.879L21.415 17L23.536 19.122L22.122 20.536L20 18.414L17.88 20.536L16.465 19.121L18.586 17.001L16.465 14.879L17.879 13.465L20 15.586L22.122 13.465ZM3 3H21L14 12.817V21H10V12.817L3 3Z"
                fill="#446DB5"
              />
            </svg>
          </span>
          Clear filter
        </button>
      </div>

      {/* Selected Tags Row */}
      <div className="flex flex-wrap gap-2">
        {filterConfigs.map((filter) =>
          selectedFilters[filter.key].map((option) => (
            <span
              key={option}
              className={`flex items-center gap-1 bg-${filter.color}-100 text-${filter.color}-700 px-2 py-0.5 rounded-full text-xs`}
            >
              {option}
              <XMarkIcon
                className="w-3 h-3 cursor-pointer"
                onClick={() =>
                  setFilter(
                    filter.key,
                    selectedFilters[filter.key].filter((o) => o !== option)
                  )
                }
              />
            </span>
          ))
        )}
      </div>
    </div>
  );
}
